import { create } from "zustand";
import { AudioManager } from "@/engine/AudioManager";

/**
 * 오디오 사용자 설정을 AudioManager 와 동기화하는 상태. 설정 씬이 이 스토어를 조작.
 * AudioManager 는 싱글톤이라 직접 조작해도 되지만, UI 반응성을 위해 Zustand 로 미러링.
 */

interface AudioState {
    master: number;
    bgm: number;
    sfx: number;
    muted: boolean;
}

interface AudioActions {
    setMaster: (v: number) => void;
    setBgm: (v: number) => void;
    setSfx: (v: number) => void;
    toggleMute: () => void;
    setMuted: (v: boolean) => void;
}

const STORAGE_KEY = "rpg.audioSettings";

function loadFromStorage(): AudioState | null {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) as AudioState : null;
    } catch {
        return null;
    }
}

function persistToStorage(state: AudioState) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch { /* storage quota */ }
}

const initial: AudioState = loadFromStorage() ?? {
    master: 0.8,
    bgm: 0.55,
    sfx: 0.9,
    muted: false,
};

AudioManager.setMaster(initial.master);
AudioManager.setBgmGain(initial.bgm);
AudioManager.setSfxGain(initial.sfx);
AudioManager.setMuted(initial.muted);

export const useAudio = create<AudioState & AudioActions>((set, get) => ({
    ...initial,

    setMaster: (v) => {
        AudioManager.setMaster(v);
        set({ master: v });
        persistToStorage({ ...get(), master: v });
    },
    setBgm: (v) => {
        AudioManager.setBgmGain(v);
        set({ bgm: v });
        persistToStorage({ ...get(), bgm: v });
    },
    setSfx: (v) => {
        AudioManager.setSfxGain(v);
        set({ sfx: v });
        persistToStorage({ ...get(), sfx: v });
    },
    toggleMute: () => {
        const next = !get().muted;
        AudioManager.setMuted(next);
        set({ muted: next });
        persistToStorage({ ...get(), muted: next });
    },
    setMuted: (v) => {
        AudioManager.setMuted(v);
        set({ muted: v });
        persistToStorage({ ...get(), muted: v });
    },
}));
